import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, UsersRound, GraduationCap, FileSignature, Radar, MapPinned,
  ClipboardList, Camera, FileText, Receipt, UserCog, BarChart3, Settings, LogOut, Shield,
  Briefcase, Map, MessagesSquare,
} from 'lucide-react';

// Iter120L: every role EXCEPT 'client'. Used to gate modules that clients
// must never see per Phase 4/5/13 of the architecture update.
const STAFF_ROLES = [
  'main_hub', 'super_admin', 'department_admin', 'training_director',
  'field_investigator', 'skip_tracer', 'process_server', 'trainee',
];

const SECTIONS = [
  {
    label: 'Operations',
    items: [
      { path: '/dashboard',             label: 'Dashboard',     icon: LayoutDashboard },
      { path: '/cases',                 label: 'Cases',         icon: FolderKanban },
      { path: '/messages',              label: 'Messages',      icon: MessagesSquare },
      { path: '/case-acceptance',       label: 'Acceptance',    icon: FileSignature, roles: STAFF_ROLES },
      { path: '/investigator-tracking', label: 'GPS Tracking',  icon: MapPinned,     roles: STAFF_ROLES },
      { path: '/case-operations',       label: 'Operations Map',icon: Map,           roles: STAFF_ROLES },
    ],
  },
  {
    label: 'Field',
    items: [
      { path: '/skip-trace',  label: 'Skip Trace',  icon: Radar,        roles: STAFF_ROLES },
      { path: '/case-logs',   label: 'Field Logs',  icon: ClipboardList },
      { path: '/evidence',    label: 'Evidence',    icon: Camera },
      { path: '/reports',     label: 'Reports',     icon: FileText },
    ],
  },
  {
    label: 'Personnel',
    items: [
      { path: '/nexus-new-hire',  label: 'Recruitment', icon: UsersRound,    roles: ['department_admin', 'main_hub', 'super_admin'] },
      { path: '/training-center', label: 'Training',    icon: GraduationCap, gate: 'training_center_access', roles: STAFF_ROLES },
      { path: '/team',            label: 'Team',        icon: UserCog,       roles: ['department_admin'] },
    ],
  },
  {
    label: 'Clients',
    items: [
      { path: '/clients',  label: 'Client Management', icon: Briefcase, roles: ['department_admin', 'main_hub'] },
      { path: '/intakes',  label: 'Client Intakes',    icon: ClipboardList, roles: ['department_admin', 'main_hub'] },
      { path: '/invoices', label: 'Billing',           icon: Receipt },
    ],
  },
  {
    label: 'Admin',
    items: [
      { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['department_admin', 'main_hub'] },
      { path: '/settings',  label: 'Settings',  icon: Settings },
    ],
  },
];

const ADMIN_ROLES = new Set(['main_hub', 'department_admin', 'training_director', 'super_admin']);

export function NxRail({ user, onSignOut, open = false, onClose }) {
  const location = useLocation();
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const visible = (item) => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (item.gate === 'training_center_access') {
      if (ADMIN_ROLES.has(user?.role)) return true;
      const perms = user?.training_permissions || {};
      return !!(perms.training_center_access || user?.training_center_access);
    }
    return true;
  };

  return (
    <aside className={`nx-sb ${open ? 'nx-sb--open' : ''}`} data-testid="nx-sb">
      <Link to="/dashboard" className="nx-sb-brand" data-testid="nx-sb-brand" onClick={onClose}>
        <div className="nx-sb-brand-mark"><Shield size={20} /></div>
        <div>
          <div className="nx-sb-brand-name">NexusTrac</div>
          <span className="nx-sb-brand-tag">Investigation MDT</span>
        </div>
      </Link>

      <nav className="nx-sb-nav">
        {SECTIONS.map(section => {
          const items = section.items.filter(visible);
          if (items.length === 0) return null;
          return (
            <div key={section.label}>
              <div className="nx-sb-section">{section.label}</div>
              {items.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nx-sb-item ${active ? 'nx-sb-item--active' : ''}`}
                    data-testid={`nx-sb-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={onClose}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="nx-sb-foot">
        <button
          type="button" onClick={onSignOut}
          className="nx-sb-item w-full" data-testid="nx-sb-signout"
          style={{ background: 'transparent', cursor: 'pointer' }}
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
